import React from 'react';
import { PlusIcon, ExclamationTriangleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface SequenceEmptyStateProps {
  type: 'no-sequences' | 'no-results' | 'error';
  errorMessage?: string;
  onClearFilters?: () => void;
  onCreateSequence?: () => void;
}

export const SequenceEmptyState: React.FC<SequenceEmptyStateProps> = ({
  type,
  errorMessage,
  onClearFilters,
  onCreateSequence,
}) => {
  if (type === 'error') {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading sequences</h3>
        <p className="mt-1 text-sm text-gray-500">
          {errorMessage || 'An unexpected error occurred while loading sequences.'}
        </p>
      </div>
    );
  }

  if (type === 'no-results') {
    return (
      <div className="text-center py-12">
        <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No sequences found</h3>
        <p className="mt-1 text-sm text-gray-500">
          No sequences match your current filters. Try adjusting your search criteria.
        </p>
        {onClearFilters && (
          <div className="mt-6">
            <button
              type="button"
              onClick={onClearFilters}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    );
  }

  // no-sequences
  return (
    <div className="text-center py-12">
      <svg
        className="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <h3 className="mt-2 text-sm font-medium text-gray-900">No sequences</h3>
      <p className="mt-1 text-sm text-gray-500">
        Get started by creating your first multi-step message sequence.
      </p>
      {onCreateSequence && (
        <div className="mt-6">
          <button
            type="button"
            onClick={onCreateSequence}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Create Sequence
          </button>
        </div>
      )}
    </div>
  );
};

